import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { clientApi, FIT_VERDICT_LABEL } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Search, Loader2, FileText, ArrowRight, ShieldCheck } from 'lucide-react';
import { useSort } from '@/lib/useSort';
import { SortableHeader } from '@/components/shared/SortableHeader';
import { ListGridToggle } from '@/components/shared/ListGridToggle';
import i18n from 'i18next';

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(
      i18n.language?.startsWith('es') ? 'es-PA' : 'en-US',
      { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
};

const SUITE_LABEL = {
  talentmap: 'TalentMap',
  talentcheck: 'TalentCheck',
  climara: 'Climara',
  talentcompetency: 'TalentCompetency',
  talenttrain: 'TalentTrain',
  talentstart: 'TalentStart',
  talentfix: 'TalentFix',
  talentheart: 'TalentHeart',
  workforce_records: 'Workforce Records',
};
const SUITE_TONE = {
  talentmap: 'bg-primary/10 text-primary border-primary/20',
  climara: 'bg-warning/10 text-warning border-warning/20',
  talentcompetency: 'bg-accent/10 text-accent border-accent/20',
  talenttrain: 'bg-success/10 text-success border-success/20',
};

export default function ClientReports() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [view, setView] = useState('list');
  const sort = useSort();

  useEffect(() => {
    let alive = true;
    clientApi.listReports()
      .then((d) => { if (alive) setItems(d); })
      .catch((e) => toast.error(t('pages.clientReports.loadFailed'), { description: formatApiError(e) }))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return items;
    return items.filter((r) => (r.candidate?.name || '').toLowerCase().includes(s));
  }, [items, search]);

  const sorted = sort.apply(filtered, {
    evaluated: (r) => (r.candidate?.name || '').toLowerCase(),
    suite:     (r) => (r.suite_code || ''),
    score:     (r) => r.scores?.overall_score ?? -1,
    fit:       (r) => r.fit?.verdict || '',
    released:  (r) => r.released_at ? new Date(r.released_at).getTime() : 0,
  });

  return (
    <>
      <Topbar title={t('pages.clientReports.title')} subtitle={t('pages.clientReports.subtitle')} />
      <div className="p-6 lg:p-8 space-y-6" data-testid="client-reports-page">
        <Card className="p-5 shadow-card bg-gradient-hero text-white relative overflow-hidden">
          <div className="absolute inset-0 dot-pattern opacity-[0.08]" />
          <div className="relative">
            <Badge className="bg-white/10 text-white border-white/20 hover:bg-white/15 font-medium"><ShieldCheck className="h-3 w-3 mr-1.5" /> {t('pages.clientReports.heroBadge')}</Badge>
            <h2 className="font-display text-xl md:text-2xl font-bold mt-3">{t('pages.clientReports.heroTitle')}</h2>
            <p className="text-white/75 mt-2 max-w-2xl text-sm leading-relaxed">{t('pages.clientReports.heroBody')}</p>
          </div>
        </Card>

        <Card className="p-4 shadow-card flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-60">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input data-testid="client-reports-search" placeholder={t('pages.clientReports.searchPh')} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-10" />
          </div>
          <ListGridToggle value={view} onChange={setView} testIdPrefix="creports-view" />
        </Card>

        <Card className="shadow-card overflow-hidden">
          {loading ? <div className="p-12 flex items-center justify-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('pages.clientReports.loading')}</div>
          : sorted.length === 0 ? <div className="p-12 text-center"><FileText className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" /><p className="text-sm text-muted-foreground">{t('pages.clientReports.empty')}</p></div>
          : view === 'list' ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead><SortableHeader sort={sort} k="evaluated" testIdPrefix="creports">{t('pages.clientReports.th.evaluated')}</SortableHeader></TableHead>
                  <TableHead><SortableHeader sort={sort} k="suite" testIdPrefix="creports">{t('pages.clientReports.th.suite')}</SortableHeader></TableHead>
                  <TableHead><SortableHeader sort={sort} k="score" testIdPrefix="creports">{t('pages.clientReports.th.score')}</SortableHeader></TableHead>
                  <TableHead><SortableHeader sort={sort} k="fit" testIdPrefix="creports">{t('pages.clientReports.th.suitability')}</SortableHeader></TableHead>
                  <TableHead><SortableHeader sort={sort} k="released" testIdPrefix="creports">{t('pages.clientReports.th.released')}</SortableHeader></TableHead>
                  <TableHead>{t('pages.clientReports.th.completed', { defaultValue: 'Completed' })}</TableHead>
                  <TableHead className="text-right">{t('pages.clientReports.th.action')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((r) => (
                  <TableRow key={r.id} data-testid={`client-report-row-${r.id}`} className="cursor-pointer hover:bg-muted/40"
                            onClick={() => navigate(r.report_kind === 'climara' ? '/client/climara' : `/client/reports/${r.id}`)}>
                    <TableCell>
                      <p className="font-medium">{r.candidate?.name || r.campaign_name || '—'}</p>
                      {(r.position || r.wave_label) && <p className="text-xs text-muted-foreground">{r.position || r.wave_label}</p>}
                    </TableCell>
                    <TableCell>{r.suite_code ? <Badge variant="outline" className={SUITE_TONE[r.suite_code] || ''}>{SUITE_LABEL[r.suite_code] || r.suite_code}</Badge> : '—'}</TableCell>
                    <TableCell className="tabular-nums">{r.report_mode === 'praxora_p4' ? 'P4' : (r.report_mode === 'dimensional_profile' ? 'Perfil' : (r.scores?.overall_score ?? '—'))}</TableCell>
                    <TableCell>{r.fit?.verdict ? <Badge variant="outline">{t(`reports.verdict.${r.fit.verdict}`, { defaultValue: FIT_VERDICT_LABEL[r.fit.verdict] || r.fit.verdict })}{r.fit.score != null ? ` · ${r.fit.score}` : ''}</Badge> : '—'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmt(r.released_at)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{r.completed_at ? fmt(r.completed_at) : <span className="cursor-help" title={t('pages.clientReports.completedUnavailable', { defaultValue: 'The completion date is not available for this evaluation.' })} data-testid={`creports-completed-na-${r.id}`}>—</span>}</TableCell>
                    <TableCell className="text-right"><Button size="sm" variant="ghost" className="gap-1.5">{t('pages.clientReports.open')} <ArrowRight className="h-3.5 w-3.5" /></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4" data-testid="creports-grid">
              {sorted.map((r) => (
                <Card key={r.id} className="p-4 shadow-card hover:shadow-medium cursor-pointer"
                      onClick={() => navigate(r.report_kind === 'climara' ? '/client/climara' : `/client/reports/${r.id}`)}
                      data-testid={`creports-card-${r.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{r.candidate?.name || r.campaign_name || '—'}</p>
                      <p className="text-xs text-muted-foreground truncate">{r.position || r.wave_label}</p>
                    </div>
                    {r.suite_code && <Badge variant="outline" className={SUITE_TONE[r.suite_code] || ''}>{SUITE_LABEL[r.suite_code] || r.suite_code}</Badge>}
                  </div>
                  <div className="mt-3">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium" data-testid={`creports-score-label-${r.id}`}>
                      {t('pages.clientReports.th.score')}
                    </p>
                    <div className="flex items-baseline gap-2 mt-0.5">
                      <p className="text-2xl font-display font-bold tabular-nums">{r.report_mode === 'praxora_p4' ? 'P4' : (r.report_mode === 'dimensional_profile' ? 'Perfil' : (r.scores?.overall_score ?? '—'))}</p>
                      {r.fit && <Badge variant="outline" className="text-xs">{t(`reports.verdict.${r.fit.verdict}`, { defaultValue: FIT_VERDICT_LABEL[r.fit.verdict] })}</Badge>}
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-3">
                    {t('pages.clientReports.th.released')} · {fmt(r.released_at)}
                    {r.completed_at ? <> · {t('pages.clientReports.th.completed', { defaultValue: 'Completed' })} · {fmt(r.completed_at)}</> : null}
                  </p>
                </Card>
              ))}
            </div>
          )}
        </Card>
      </div>
    </>
  );
}
